<template>
  <div class="flex flex-col gap-2 w-full">
    <v-row dense>
      <v-col cols="12">
        <h2 class="text-left font-bold text-[18px] text-black">Lista de escalas</h2>
      </v-col>
    </v-row>

    <SearchInput
      v-model="searchTerm"
      width="100%"
      label="Buscar empresa, unidade, escala, turno ou posição"
      hide-details
    />

    <template v-if="isLoadingSchedulesForPage">
      <ListSchedulesSkeleton />
    </template>

    <template v-else>
      <div v-for="company in filteredSchedules" :key="company.id" class="flex w-full flex-col">
        <div class="flex w-full">
          <span
            @click="toggleCompanies(company.id)"
            class="header-title"
            :class="showCompanies[company.id] ? 'rounded-b-none bg-gray-200' : ''"
          >
            <div class="flex flex-col justify-start text-left font-normal">
              <span class="text-gray-500 text-xs">Empresa:</span>
              <span class="text-primary font-bold">{{ company.name.toLocaleLowerCase() }}</span>
            </div>
            <div class="flex gap-1">
              <v-icon
                v-if="canReadSchedulePolicies"
                data-testid="company-policies-button"
                :icon="mdiClipboardTextOutline"
                @click.stop="showCompanyPolicies(company.id, company.name)"
              />
              <v-icon
                v-if="canReadSharingModal"
                class="share-icon hover:cursor-pointer"
                color="#52526F"
                :icon="mdiShareVariantOutline"
                @click.stop="
                  openShareModal(
                    company.name,
                    AuthorizationResourcesAndSubjects.Company,
                    company.id,
                    company.id,
                    [company.name]
                  )
                "
              />
              <arrow-icon :class="showCompanies[company.id] ? 'rotate-180' : ''" color="#000" />
            </div>
          </span>
        </div>

        <template v-if="showCompanies[company.id]">
          <template v-for="healthUnit of company.units" :key="healthUnit.id">
            <div
              v-if="shouldShowHealthUnit(healthUnit)"
              class="card"
              @click="toggleHealthUnits(healthUnit.id)"
              :class="showHealthUnits[healthUnit.id] ? 'rounded-b-none bg-gray-100' : ''"
            >
              <div class="flex flex-col text-left">
                <span class="text-gray-500 text-xs">Unidade:</span>
                <span class="capitalize">{{ healthUnit.name.toLocaleLowerCase() }}</span>
              </div>
              <div class="flex gap-1">
                <v-tooltip text="Reordenar" location="top">
                  <template #activator="{ props }">
                    <v-icon
                      v-if="canReorderHealthUnit"
                      v-bind="props"
                      :icon="mdiSort"
                      @click.stop="handleOpenSortScalesModal(healthUnit)"
                    />
                  </template>
                </v-tooltip>
                <v-tooltip text="Políticas" location="top">
                  <template #activator="{ props }">
                    <v-icon
                      v-if="canReadSchedulePolicies"
                      v-bind="props"
                      data-testid="health-unit-policies-button"
                      :icon="mdiClipboardTextOutline"
                      @click.stop="
                        showHealthUnitPolicies(company.id, healthUnit.id, healthUnit.name)
                      "
                    />
                  </template>
                </v-tooltip>

                <v-tooltip text="Visibilidade para equipe médica" location="top">
                  <template #activator="{ props }">
                    <v-icon
                      class="col-span-1 sm:col-span-1 flex items-center justify-end mt-0 mr-3"
                      size="22"
                      v-bind="props"
                      @click.stop="requestChangeSchedulesVisibility(healthUnit)"
                      :icon="mdiAccountEye"
                    />
                  </template>
                </v-tooltip>

                <v-tooltip text="Compartilhar" location="top">
                  <template #activator="{ props }">
                    <v-icon
                      v-if="canReadSharingModal"
                      v-bind="props"
                      class="share-icon hover:cursor-pointer"
                      color="#52526F"
                      :icon="mdiShareVariantOutline"
                      @click.stop="
                        openShareModal(
                          healthUnit.name,
                          AuthorizationResourcesAndSubjects.HealthUnit,
                          healthUnit.id,
                          company.id,
                          [company.name, healthUnit.name]
                        )
                      "
                    />
                  </template>
                </v-tooltip>
                <arrow-icon
                  :class="showHealthUnits[healthUnit.id] ? 'rotate-180' : ''"
                  color="#000"
                />
              </div>

              <template v-if="showHealthUnits[healthUnit.id]">
                <template
                  v-for="schedule of sortedSchedules(healthUnit.schedules)"
                  :key="schedule.id"
                >
                  <div
                    v-if="canReadSchedule(schedule.id)"
                    @click.stop="toggleShifts(schedule.id)"
                    class="card2 bg-gray-50"
                  >
                    <div class="flex flex-col text-left">
                      <span class="text-gray-500 text-xs">Escala:</span>
                      <span class="capitalize flex items-center gap-1">
                        <v-tooltip
                          v-if="expiredScheduleSet.has(schedule.id)"
                          text="Escala expirada"
                          location="top"
                        >
                          <template #activator="{ props: tooltipProps }">
                            <LabelBadge
                              v-bind="tooltipProps"
                              label=""
                              color="orange"
                              size="small"
                              data-testid="expired-schedule-badge"
                            />
                          </template>
                        </v-tooltip>
                        {{ schedule.name.toLocaleLowerCase() }}
                      </span>
                      <div class="flex flex-wrap md:gap-2 items-center text-gray-500 text-sm">
                        <span>{{ getTimezoneDetails(schedule.timezone)?.title }}</span>
                        <span>({{ getTimezoneDetails(schedule.timezone)?.subtitle }})</span>
                      </div>
                    </div>
                    <span class="flex gap-1 justify-center items-center">
                      <v-tooltip
                        v-if="policyAlertSet.has(schedule.id)"
                        text="Existe(m) política(s) sem vigência"
                        location="top"
                      >
                        <template #activator="{ props: tooltipProps }">
                          <LabelBadge
                            v-bind="tooltipProps"
                            label=""
                            color="red"
                            size="small"
                            data-testid="policy-alert-badge"
                          />
                        </template>
                      </v-tooltip>
                      <v-icon
                        v-if="canReadSharingModal"
                        class="share-icon hover:cursor-pointer"
                        color="#52526F"
                        :icon="mdiShareVariantOutline"
                        @click.stop="
                          openShareModal(
                            schedule.name,
                            AuthorizationResourcesAndSubjects.Schedule,
                            schedule.id,
                            company.id,
                            [company.name, healthUnit.name, schedule.name]
                          )
                        "
                      />
                      <edit-icon
                        v-if="canEditSchedule"
                        @click.stop="
                          handleUpdate({
                            id: schedule.id,
                            name: schedule.name,
                            healthUnitName: healthUnit.name,
                            companyName: company.name,
                            companyId: company.id,
                            endDate: schedule.endDate ?? null
                          })
                        "
                        class="cursor-pointer"
                      />
                      <delete-icon
                        v-if="canDeactivateSchedule"
                        @click.stop="handleRemove(schedule.id, schedule.name)"
                        class="cursor-pointer"
                      />
                      <div class="flex gap-1" v-if="schedule.shifts.length > 0">
                        <arrow-icon
                          :class="showShifts[schedule.id] ? 'rotate-180' : ''"
                          color="#52526F"
                          @click.stop="toggleShifts(schedule.id)"
                          class="cursor-pointer"
                        />
                      </div>
                    </span>

                    <template v-if="showShifts[schedule.id]">
                      <div
                        v-for="shift of schedule.shifts"
                        :key="shift.id"
                        class="card2 bg-gray-100"
                        @click.stop="togglePositions(shift.id)"
                      >
                        <div class="flex flex-col text-left">
                          <span class="text-gray-500 text-xs">Turno:</span>
                          <span class="capitalize">{{ shift.name.toLocaleLowerCase() }}</span>
                        </div>
                        <span class="flex gap-1 justify-center items-center">
                          <div class="flex gap-1" v-if="shift.positions.length > 0">
                            <arrow-icon
                              :class="showPositions[shift.id] ? 'rotate-180' : ''"
                              color="#52526F"
                              @click.stop="togglePositions(shift.id)"
                              class="cursor-pointer"
                            />
                          </div>
                        </span>

                        <template v-if="showPositions[shift.id]">
                          <div
                            v-for="position of shift.positions"
                            :key="position.id"
                            class="card2 bg-white"
                          >
                            <div class="flex flex-col text-left">
                              <span class="text-gray-500 text-xs">Posição:</span>
                              <span class="capitalize">{{
                                position.name.toLocaleLowerCase()
                              }}</span>
                            </div>
                            <span class="flex gap-1 justify-center items-center">
                              <v-tooltip text="Políticas" location="top">
                                <template #activator="{ props: tooltipProps }">
                                  <v-icon
                                    v-if="canReadSchedulePolicies"
                                    v-bind="tooltipProps"
                                    data-testid="position-policies-button"
                                    :icon="mdiClipboardTextOutline"
                                    @click.stop="
                                      showPositionPolicies(company.id, position.id, position.name)
                                    "
                                  />
                                </template>
                              </v-tooltip>
                              <edit-icon
                                v-if="canEditSchedulePosition"
                                @click.stop="handleUpdatePosition(position.id, schedule.id)"
                                class="cursor-pointer"
                              />
                              <delete-icon
                                v-if="canDeactivateSchedulePosition"
                                @click.stop="handleRemovePosition(position.id)"
                                class="cursor-pointer"
                              />
                            </span>
                          </div>
                        </template>
                      </div>
                    </template>
                  </div>
                </template>
              </template>
            </div>
          </template>
        </template>
      </div>

      <div
        v-if="isSearchActive && filteredSchedules.length === 0"
        class="text-center py-8 text-gray-500"
      >
        Nenhum resultado encontrado para "{{ searchTerm }}"
      </div>
    </template>

    <SortScalesModal
      v-if="healthUnitSelected"
      :modalActive="showModalSortScales"
      :unitData="healthUnitSelected"
      @close="showModalSortScales = false"
    />

    <ResourcePoliciesModal v-if="policiesTarget" :resource="policiesTarget" />
    <share-modal />

    <HealthUnitScheduleVisibilityModal
      v-if="toggleScheduleVisibilityModal"
      :healthUnit="healthUnitToUpdate"
      :modalStateProp="toggleScheduleVisibilityModal"
      @close="closeScheduleVisibilityModal"
    />
  </div>
</template>
<script setup lang="ts">
import ArrowIcon from '@/assets/icons/down.vue'
import EditIcon from '@/assets/icons/edit.vue'
import DeleteIcon from '@/assets/icons/delete.vue'
import ListSchedulesSkeleton from '@/components/designSystem/Skeleton/ListSchedulesSkeleton.vue'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import Network from '@/Network'
import { mdiShareVariantOutline, mdiAccountEye } from '@mdi/js'
import { ref, computed, watch, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { timezones } from '@/hooks/useCommon'
import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import ShareModal from '@/components/designSystem/modals/share.modal.vue'
import ResourcePoliciesModal from '@/components/designSystem/modals/ResourcePoliciesModal/index.vue'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { mdiClipboardTextOutline, mdiSort } from '@mdi/js'
import SortScalesModal, { UnitData } from './modals/SortScalesModal.vue'
import { SimpleHealthUnitResponse } from '@/Network/Types/Responses/HealthUnitResponses'
import {
  ListScheduleItem,
  ListUnitScheduleItem
} from '@/Network/Types/Responses/ScheduleUnitResponses'
import HealthUnitScheduleVisibilityModal from './modals/health.unit.schedule.visibility.modal.vue'
import {
  AuthorizationPermissionCheck,
  AuthorizationResourcesAndSubjects
} from '@/types/authorization'

const emit = defineEmits([
  'removeSchedule',
  'updateSchedule',
  'showPositions',
  'showCompanyPolicies',
  'showHealthUnitPolicies',
  'showPositionPolicies',
  'removePosition',
  'updatePosition'
])

const modalStore = useModalStore()
const showCompanies = ref<Record<string, boolean>>({})
const showHealthUnits = ref<Record<string, boolean>>({})
const showShifts = ref<Record<number, boolean>>({})
const showPositions = ref<Record<number, boolean>>({})
const { schedulesForPage, isLoadingSchedulesForPage } = storeToRefs(useScheduleStore())

const searchTerm = ref('')

const policyAlertSet = ref<Set<number>>(new Set())
const expiredScheduleSet = ref<Set<number>>(new Set())

function sortedSchedules(schedules: (ListScheduleItem & { timezone: string })[]) {
  return [...schedules].sort((a, b) => {
    const aExpired = expiredScheduleSet.value.has(a.id) ? 1 : 0
    const bExpired = expiredScheduleSet.value.has(b.id) ? 1 : 0
    if (aExpired !== bExpired) return aExpired - bExpired
    return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
  })
}

async function fetchPolicyVigencyStatus() {
  try {
    const { invalidPolicies, expiredSchedules } = await Network.schedule.getPolicyVigencyStatus()
    policyAlertSet.value = new Set(invalidPolicies)
    expiredScheduleSet.value = new Set(expiredSchedules)
  } catch (error) {
    // fallback intencional: os selos de política são decoração da linha da escala.
    // Um toast/estado de erro aqui bloquearia a listagem — que carrega por conta
    // própria e tem estado de erro próprio (`loadError` de useScheduleStore) —
    // por causa de um indicador auxiliar. Sem os selos, a lista segue utilizável.
    console.error('Falha ao verificar a vigência das políticas das escalas:', error)
    policyAlertSet.value = new Set()
    expiredScheduleSet.value = new Set()
  }
}

onMounted(() => {
  if (!isLoadingSchedulesForPage.value && schedulesForPage.value.length > 0) {
    fetchPolicyVigencyStatus()
  }
})

watch(isLoadingSchedulesForPage, (isLoading, wasLoading) => {
  if (wasLoading && !isLoading && schedulesForPage.value.length > 0) {
    fetchPolicyVigencyStatus()
  }
})

watch(
  [
    () => modalStore.modals[EModals.EDIT_SCHEDULE],
    () => modalStore.modals[EModals.RESOURCE_POLICIES_MODAL]
  ],
  ([editOpen, resourcePoliciesOpen], [wasEditOpen, wasResourcePoliciesOpen]) => {
    if ((wasEditOpen && !editOpen) || (wasResourcePoliciesOpen && !resourcePoliciesOpen)) {
      fetchPolicyVigencyStatus()
    }
  }
)

const toggleScheduleVisibilityModal = ref(false)
const healthUnitToUpdate = ref<SimpleHealthUnitResponse>()
const policiesTarget = ref<{
  type: AuthorizationResourcesAndSubjects
  id: number
  name: string
  companyId: number
} | null>(null)

const permissionsStore = usePermissionsStore()

const check = (permission: string, type: string, id: string | number) => {
  const checkObject: AuthorizationPermissionCheck = {
    permission: permission as any,
    resource: { objectType: type as any, objectId: Number(id) }
  }
  return permissionsStore.check(checkObject)
}

const checkAny = (permission: string, type: string) => {
  return permissionsStore.checkAnyRessources({
    permission: permission as any,
    resourceType: type as any
  })
}

const canReadSchedulePolicies = checkAny('schedule_payment_policies_links_reading', 'schedule')
const canReadSharingModal = checkAny('sharing_modal_reading_grant_access', 'company')
const canReorderHealthUnit = checkAny('health_unit_reordering', 'health_unit')
const canEditSchedule = checkAny('schedule_editing', 'schedule')
const canDeactivateSchedule = checkAny('schedule_deactivating', 'schedule')
const canEditSchedulePosition = checkAny('schedule_position_editing', 'schedule')
const canDeactivateSchedulePosition = checkAny('schedule_position_deactivating', 'schedule')

const canReadSchedule = (id: number) => check('schedule_basic_reading', 'schedule', id)

const shouldShowHealthUnit = (healthUnit: any) => {
  const hasUnitPermission = check('health_unit_basic_reading', 'health_unit', healthUnit.id)

  if (hasUnitPermission) return true

  return healthUnit.schedules.some((schedule: any) =>
    check('schedule_basic_reading', 'schedule', schedule.id)
  )
}

const normalize = (text: string) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const isSearchActive = computed(() => normalize(searchTerm.value).length > 0)

function notNull<T>(value: T | null): value is T {
  return value !== null
}

const filteredSchedules = computed(() => {
  const term = normalize(searchTerm.value)
  if (!term) return schedulesForPage.value

  return schedulesForPage.value
    .map((company) => {
      const companyMatch = normalize(company.name).includes(term)

      const filteredUnits = company.units
        .filter((unit) => shouldShowHealthUnit(unit))
        .map((unit) => {
          const unitMatch = normalize(unit.name).includes(term)

          const filteredUnitSchedules = unit.schedules
            .filter((s) => canReadSchedule(s.id))
            .map((schedule) => {
              const scheduleMatch = normalize(schedule.name).includes(term)

              const filteredShifts = schedule.shifts
                .map((shift) => {
                  const shiftMatch = normalize(shift.name).includes(term)
                  const filteredPositions = shift.positions.filter((pos) =>
                    normalize(pos.name).includes(term)
                  )

                  if (shiftMatch || filteredPositions.length > 0) {
                    return { ...shift, positions: shiftMatch ? shift.positions : filteredPositions }
                  }
                  return null
                })
                .filter(notNull)

              if (companyMatch || unitMatch || scheduleMatch || filteredShifts.length > 0) {
                return {
                  ...schedule,
                  shifts:
                    companyMatch || unitMatch || scheduleMatch ? schedule.shifts : filteredShifts
                }
              }
              return null
            })
            .filter(notNull)

          if (companyMatch || unitMatch || filteredUnitSchedules.length > 0) {
            return {
              ...unit,
              schedules: companyMatch || unitMatch ? unit.schedules : filteredUnitSchedules
            }
          }
          return null
        })
        .filter(notNull)

      if (companyMatch || filteredUnits.length > 0) {
        return { ...company, units: companyMatch ? company.units : filteredUnits }
      }
      return null
    })
    .filter(notNull)
})

let searchDebounceTimer: ReturnType<typeof setTimeout>

watch(searchTerm, (val) => {
  clearTimeout(searchDebounceTimer)
  searchDebounceTimer = setTimeout(() => {
    if (!normalize(val)) {
      showCompanies.value = {}
      showHealthUnits.value = {}
      showShifts.value = {}
      showPositions.value = {}
      return
    }

    const companies: Record<string, boolean> = {}
    const units: Record<string, boolean> = {}
    const shifts: Record<number, boolean> = {}
    const positions: Record<number, boolean> = {}

    for (const company of filteredSchedules.value) {
      companies[company.id] = true
      for (const unit of company.units) {
        units[unit.id] = true
        for (const schedule of unit.schedules) {
          shifts[schedule.id] = true
          for (const shift of schedule.shifts) {
            positions[shift.id] = true
          }
        }
      }
    }

    showCompanies.value = companies
    showHealthUnits.value = units
    showShifts.value = shifts
    showPositions.value = positions
  }, 300)
})

const showModalSortScales = ref(false)
const healthUnitSelected = ref<UnitData | null>(null)

const handleOpenSortScalesModal = (healthUnit: ListUnitScheduleItem) => {
  const formattedUnitData: UnitData = {
    id: healthUnit.id,
    name: healthUnit.name,
    schedules: healthUnit.schedules.map((schedule) => ({
      id: schedule.id,
      name: schedule.name,
      shifts: schedule.shifts.map((shift) => ({
        id: shift.id,
        name: shift.name,
        positions: shift.positions || []
      }))
    }))
  }

  healthUnitSelected.value = formattedUnitData
  showModalSortScales.value = true
}

const toggleCompanies = (id: number) => {
  showCompanies.value = { ...showCompanies.value, [id]: !showCompanies.value[id] }
}

const toggleHealthUnits = (id: number) => {
  showHealthUnits.value = { ...showHealthUnits.value, [id]: !showHealthUnits.value[id] }
}

const toggleShifts = (scheduleId: number) => {
  showShifts.value = { ...showShifts.value, [scheduleId]: !showShifts.value[scheduleId] }
}

const togglePositions = (shiftId: number) => {
  showPositions.value = { ...showPositions.value, [shiftId]: !showPositions.value[shiftId] }
}

const getTimezoneDetails = (time: string) => {
  return timezones.find((t) => t.value === time)
}

const handleRemove = (id: number, name: string) => {
  emit('removeSchedule', { id, name })
  modalStore.toggleModal(EModals.REMOVE_SCHEDULE)
}

const handleUpdate = (scheduleDetails: object) => {
  emit('updateSchedule', scheduleDetails)
  modalStore.toggleModal(EModals.EDIT_SCHEDULE)
}

const handleUpdatePosition = (positionId: number, scheduleId: number) => {
  emit('updatePosition', { scheduleId: scheduleId, positionId: positionId })
  modalStore.toggleModal(EModals.UPDATE_POSITION)
}

const handleRemovePosition = (id: number) => {
  emit('removePosition', id)
  modalStore.toggleModal(EModals.REMOVE_POSITION)
}

const openShareModal = (
  resourceName: string,
  resourceType: AuthorizationResourcesAndSubjects,
  resourceId: string | number,
  companyId?: number,
  breadcrumb: string[] = []
) => {
  const resolvedCompanyId =
    companyId ??
    (resourceType === AuthorizationResourcesAndSubjects.Company ? Number(resourceId) : undefined)

  modalStore.metadata = {
    resourceName: resourceName,
    resourceType: resourceType,
    resourceId: +resourceId,
    companyId: resolvedCompanyId,
    breadcrumb: breadcrumb
  }
  modalStore.toggleModal(EModals.SHARE)
}

const showCompanyPolicies = (companyId: number, companyName: string) => {
  emit('showCompanyPolicies', companyId)
  policiesTarget.value = {
    type: AuthorizationResourcesAndSubjects.Company,
    id: companyId,
    name: companyName,
    companyId: companyId
  }
  modalStore.toggleModal(EModals.RESOURCE_POLICIES_MODAL)
}

const showHealthUnitPolicies = (
  companyId: number,
  healthUnitId: number,
  healthUnitName: string
) => {
  emit('showHealthUnitPolicies', healthUnitId)
  policiesTarget.value = {
    type: AuthorizationResourcesAndSubjects.HealthUnit,
    id: healthUnitId,
    name: healthUnitName,
    companyId: companyId
  }
  modalStore.toggleModal(EModals.RESOURCE_POLICIES_MODAL)
}

const showPositionPolicies = (companyId: number, positionId: number, positionName: string) => {
  emit('showPositionPolicies', positionId)
  policiesTarget.value = {
    type: AuthorizationResourcesAndSubjects.Position,
    id: positionId,
    name: positionName,
    companyId: companyId
  }
  modalStore.toggleModal(EModals.RESOURCE_POLICIES_MODAL)
}

function requestChangeSchedulesVisibility(healthUnit) {
  healthUnitToUpdate.value = healthUnit
  toggleScheduleVisibilityModal.value = true
}

const closeScheduleVisibilityModal = async () => {
  toggleScheduleVisibilityModal.value = false
}
</script>

<style scoped>
.share-icon :deep(svg) {
  width: 22px;
  height: 22px;
}

.header-title {
  @apply flex justify-between items-center gap-4 w-full px-[12px] py-[16px] rounded-lg bg-[#FAFAFA] text-black text-[16px] font-bold cursor-pointer capitalize;
}
.card,
.card2 {
  @apply w-full flex flex-wrap gap-4 items-center justify-between px-[12px] py-[10px] bg-[#FAFAFA] cursor-pointer capitalize;
}
.card2 {
  @apply p-2 cursor-default rounded-lg;
}
.card:hover {
  @apply bg-gray-200;
}
</style>
